/**
 * Toast Action Handlers - Mapea notificaciones a sus acciones correspondientes
 * Ejemplo: Notificación de pedido recibido → Navigate a /orden/:orderId
 */

export interface ToastActionConfig {
  notificationType: string;
  action: (data?: any) => void;
}

/**
 * Mapea tipos de notificaciones a su función de navegación/acción
 */
export function getToastActionForNotification(
  notificationType: string,
  data?: any,
  navigate?: (path: string) => void,
  openOrderSidebar?: (orderId: string) => void
): (() => void) | undefined {
  if (!navigate) return undefined;

  const openOrder = (orderId: string) => {
    if (openOrderSidebar) {
      openOrderSidebar(orderId);
    } else {
      navigate(`/orden/${orderId}`);
    }
  };

  const actionMap: Record<string, () => void> = {
    // Órdenes
    'order_received': () => {
      if (data?.order_id) openOrder(data.order_id);
    },
    'order_confirmed': () => {
      if (data?.order_id) openOrder(data.order_id);
    },
    'order_ready': () => {
      if (data?.order_id) openOrder(data.order_id);
    },
    'order_completed': () => {
      if (data?.order_id) openOrder(data.order_id);
    },
    'order_cancelled': () => {
      if (data?.order_id) openOrder(data.order_id);
    },
    'order_status_changed': () => {
      if (data?.order_id) openOrder(data.order_id);
    },
    
    // Entregas
    'delivery_arrived': () => {
      if (data?.order_id) openOrder(data.order_id);
    },
    'delivery_completed': () => {
      if (data?.order_id) openOrder(data.order_id);
    },
    'delivery_failed': () => {
      if (data?.order_id) openOrder(data.order_id);
    },
    
    // Mensajes
    'new_message': () => {
      if (data?.conversation_id) {
        navigate(`/chat?conversation=${data.conversation_id}`);
      } else {
        navigate('/chat');
      }
    },
    'direct_message': () => {
      if (data?.user_id) {
        navigate(`/chat?user=${data.user_id}`);
      } else {
        navigate('/chat');
      }
    },
    
    // Notas/Contenido
    'note_liked': () => {
      if (data?.note_id) {
        navigate(`/nota/${data.note_id}`);
      }
    },
    'note_commented': () => {
      if (data?.note_id) {
        navigate(`/nota/${data.note_id}`);
      }
    },
    'note_shared': () => {
      if (data?.note_id) {
        navigate(`/nota/${data.note_id}`);
      }
    },
    
    // Seguidores
    'new_follower': () => {
      if (data?.user_id) {
        navigate(`/perfil/${data.user_id}`);
      } else {
        navigate('/followers');
      }
    },
    'follow_accepted': () => {
      navigate('/seguidos');
    },

    // Carrito compartido
    'shared_cart': () => {
      if (data?.link) {
        navigate(data.link);
      } else {
        navigate('/notificaciones');
      }
    },
    
    // Promociones
    'promo_expiring': () => {
      if (data?.note_id) {
        navigate(`/nota/${data.note_id}`);
      } else {
        navigate('/notas');
      }
    },
    'promo_expired': () => {
      navigate('/notas');
    },
    
    // Notificaciones genéricas de orden (incluye alquiler de tienda con link a la oficina)
    'order_update': () => {
      if (data?.link) {
        navigate(data.link);
      } else {
        navigate('/notificaciones');
      }
    },

    // Notificaciones genéricas (por defecto abrir página de notificaciones)
    'default': () => {
      navigate('/notificaciones');
    },
  };

  return actionMap[notificationType] || actionMap['default'];
}

/**
 * Mapea mensajes de notificación a tipos de notificación
 * Útil si necesitas deducir el tipo del mensaje
 */
export function inferNotificationTypeFromMessage(message: string): string {
  const lowerMessage = message.toLowerCase();

  if (lowerMessage.includes('pedido') || lowerMessage.includes('orden')) {
    if (lowerMessage.includes('recibido') || lowerMessage.includes('nueva')) {
      return 'order_received';
    } else if (lowerMessage.includes('confirmado')) {
      return 'order_confirmed';
    } else if (lowerMessage.includes('listo') || lowerMessage.includes('preparado')) {
      return 'order_ready';
    } else if (lowerMessage.includes('completado') || lowerMessage.includes('entregado')) {
      return 'order_completed';
    } else if (lowerMessage.includes('cancelado')) {
      return 'order_cancelled';
    } else if (lowerMessage.includes('estado')) {
      return 'order_status_changed';
    }
  }

  if (lowerMessage.includes('mensaje')) {
    return 'new_message';
  }

  if (lowerMessage.includes('entrega')) {
    if (lowerMessage.includes('llegó') || lowerMessage.includes('llego')) {
      return 'delivery_arrived';
    } else if (lowerMessage.includes('completada')) {
      return 'delivery_completed';
    } else if (lowerMessage.includes('falló') || lowerMessage.includes('fallo')) {
      return 'delivery_failed';
    }
  }

  if (lowerMessage.includes('me gusta') || lowerMessage.includes('like')) {
    return 'note_liked';
  }

  if (lowerMessage.includes('comentario') || lowerMessage.includes('comment')) {
    return 'note_commented';
  }

  if (lowerMessage.includes('seguidor') || lowerMessage.includes('follow')) {
    return 'new_follower';
  }

  if (lowerMessage.includes('compartido') && (lowerMessage.includes('carrito') || lowerMessage.includes('canasto'))) {
    return 'shared_cart';
  }

  if (lowerMessage.includes('promo')) {
    if (lowerMessage.includes('vence') || lowerMessage.includes('expira')) {
      return 'promo_expiring';
    }
  }

  return 'default';
}
